import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createWidget, copyWidgets, cutWidgets } from "actions/editor";

import { EditorContext } from "components/Editor";
import StoryboardContext from "components/Storyboard/context";

import _ from "lodash";
import { Frame } from "scenejs";
import { ANCHOR, CAROUSEL, CONTENT, LIVE_ELEMENT, MENU, SECTION, SECTION_COLUMN } from "constants/widgetTypes";

export default function ClipboardManager(props) {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const storyboardContext = React.useContext(StoryboardContext);
  const storyboardType = _.get(storyboardContext, 'type');
  const workspace = useSelector(state => _.get(state, 'workspaces.workspace.item', {}));
  const activePageSection = useSelector(state => _.get(state, `${editorContext.editorSession.id}.present.interactive.active`));
  const entityType = useSelector(state => _.get(state, `${editorContext.editorSession.id}.present.entityType`));

  const pasteStateReference = React.useRef({
    page: null,
    scrollPosition: null,
    offset: 1
  });

  useEffect(() => {
    editorContext.editorSession.eventBus.on('copy', handleCopy);
    editorContext.editorSession.eventBus.on('cut', handleCut);
    editorContext.editorSession.eventBus.on('paste', handlePaste);

    return () => {
      editorContext.editorSession.eventBus.off('copy', handleCopy);
      editorContext.editorSession.eventBus.off('cut', handleCut);
      editorContext.editorSession.eventBus.off('paste', handlePaste);
    }

  }, [activePageSection]);

  const handleCopy = (e) => {
    const request = prepareCopyRequest();
    dispatch(copyWidgets(editorContext.editorSession.id, request));
    pasteStateReference.current = {
      page: activePageSection,
      scrollPosition: request.scrollPosition,
      offset: 1
    };
  }

  const handleCut = (e) => {
    const request = prepareCopyRequest();
    dispatch(cutWidgets(editorContext.editorSession.id, request));
    pasteStateReference.current = {
      page: activePageSection,
      scrollPosition: request.scrollPosition,
      offset: 1
    };
  }

  const handlePaste = (e) => {
    const clipboardData = window.localStorage.getItem('CLIPBOARD');
    if(!clipboardData){
      return;
    }

    var request = JSON.parse(clipboardData);
    var widgets = preparePasteRequest(activePageSection, request);

    if(!widgets) {
      return;
    }

    dispatch(createWidget(editorContext.editorSession.id, widgets, activePageSection));
  }

  const prepareCopyRequest = () => {
    const scrollContainer = document.querySelector(`#${editorContext.editorSession.id} .editor_widget_scroll_container`);

    var request = {
      storyboard: _.get(storyboardContext, 'id'),
      workspace: _.get(workspace, 'id', null),
      scrollPosition: {
        top: scrollContainer.scrollTop,
        left: scrollContainer.scrollLeft
      }
    }

    return request;
  }

  const preparePasteRequest = (pageWidget, request) => {
    const scrollContainer = document.querySelector(`#${editorContext.editorSession.id} .editor_widget_scroll_container`);
    const top = scrollContainer.scrollTop;
    const left = scrollContainer.scrollLeft;
    
    const originalTop = _.get(request, 'scrollPosition.top', 0);
    const originalLeft = _.get(request, 'scrollPosition.left', 0);
    
    const currentScrollPosition = {
      top: top,
      left: left
    }

    var lastPastedPage = pasteStateReference.current.page;
    var offset = pasteStateReference.current.offset;
    if(lastPastedPage != pageWidget || viewportPositionChanged(currentScrollPosition, pasteStateReference.current.scrollPosition)){
      offset = 0;
    }

    var widgets = request.widgets || [];

    var ignoreTypes = [];
    if (storyboardType === 'INTERACTIVE_CONTENT' || entityType === 'GATING') {
      ignoreTypes = [MENU]
    }

    if (entityType === 'LIVE_ELEMENT') {
      ignoreTypes = [...ignoreTypes, MENU, LIVE_ELEMENT, SECTION, SECTION_COLUMN, CONTENT, CAROUSEL, ANCHOR];
    }
    
    if (workspace.id !== request.workspace) {
      ignoreTypes = [...ignoreTypes, LIVE_ELEMENT]
    }

    widgets = widgets.filter(item => !_.includes(ignoreTypes, item.type));

    if(_.size(widgets) === 0){
      return null;
    }

    var processedWidgets = widgets.map(widget => {
      var layout = _.get(widget, 'properties.desktop.layout') || {};
      const positon = new Frame(layout);
      const value = positon.get("transform", "translate").split(",").map(str => {
        str = str.trim();
        return parseFloat(str);
      });

      const x = value[0] - originalLeft + left + 20 * offset;
      const y = value[1] - originalTop + top  + 20 * offset;
      positon.set('transform', 'translate', `${x}px, ${y}px`);

      widget = _.set(widget, 'properties.desktop.layout', positon.get());
      return widget;

    }).sort((w1, w2) => {
      const o1 = _.get(w1, 'order') || 1;
      const o2 = _.get(w2, 'order') || 1;
  
      return o1 - o2;
    });

    pasteStateReference.current = {
      page: pageWidget,
      scrollPosition: currentScrollPosition,
      offset: offset + 1
    };

    return processedWidgets;
  }

  const viewportPositionChanged = (current, previous) => {
    if(!previous){
      return null;
    }

    const currentTop = _.get(current, 'top', 0);
    const currentLeft = _.get(current, 'left', 0);

    const previousTop = _.get(previous, 'top', 0);
    const previousLeft = _.get(previous, 'left', 0);

    return (currentTop != previousTop || currentLeft != previousLeft);
  }

  return false;
}